import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import type { AutomatedCampaignPayload } from '@/shared/types/campaign';
import { fetchAudienceFilter, fetchBaseUserCount } from '../api/use-campaigns';

interface Props {
  idPassbook: string;
  form: AutomatedCampaignPayload;
  clusterValues: Record<string, string | string[]>;
  owner?: boolean;
}

/** Preview de base total e filtrada — espelha cards do campaign_insert.ejs */
export function CampaignAudiencePreview({ idPassbook, form, clusterValues, owner }: Props) {
  const [baseTotal, setBaseTotal] = useState<number | null>(null);
  const [filtered, setFiltered] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBaseTotal(null);
    void fetchBaseUserCount(idPassbook, { owner }).then((n) => {
      if (!cancelled) setBaseTotal(n);
    });
    return () => {
      cancelled = true;
    };
  }, [idPassbook, owner]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const clusters = JSON.stringify(
          Object.fromEntries(
            Object.entries(clusterValues).filter(([, v]) => v && (Array.isArray(v) ? v.length : true)),
          ),
        );
        const count = await fetchAudienceFilter(
          idPassbook,
          {
            plataforma: form.plataforma,
            atividade: form.atividade,
            faixa_etaria: form.faixa_etaria,
            valor_min: form.valor_de,
            valor_max: form.valor_ate,
            genero: form.genero,
            store: form.stores,
            tipo_disparo: form.tipo_disparo,
            estado: form.state,
            cidade: form.city,
            clusters,
          },
          { owner },
        );
        setFiltered(count);
      } catch {
        setFiltered(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [idPassbook, form, clusterValues, owner]);

  return (
    <div className="rounded-xl border border-success/30 bg-success/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-success">
        <Users className="size-4" />
        <p className="text-sm font-medium">Preview da base</p>
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-xs text-muted-foreground">Base total</p>
          <p className="text-2xl font-semibold tabular-nums">
            {baseTotal != null ? baseTotal : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Campanha (filtro)</p>
          <p className="text-2xl font-semibold tabular-nums text-primary">
            {filtered != null ? filtered : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}
